import React from "react";

import { toast } from "react-toastify";
import { makeAutoObservable, autorun } from "mobx";

import { RootStore, useStores } from "@stores";
import useVM from "@src/hooks/useVM";
import {
  Farm,
  listFarms,
  createFarm,
  renameFarm,
  deleteFarm,
} from "@services/farmsService";

const STORAGE_KEY = "gg.currentFarmId";

class FarmStore {
  loading: boolean = true;
  private setLoading = (loading: boolean) => {
    this.loading = loading;
  };

  farms: Farm[] = [];
  private setFarms = (farms: Farm[]) => {
    this.farms = farms;
  };

  // Currently selected farm. Persisted across reloads so the user stays in the
  // farm they were last working in.
  currentFarmId?: string;
  setCurrentFarmId = (id: string) => {
    this.currentFarmId = id;
    try {
      window.localStorage.setItem(STORAGE_KEY, id);
    } catch {
      /* ignore storage failures */
    }
  };

  get currentFarm(): Farm | null {
    return this.farms.find((f) => f.id === this.currentFarmId) || null;
  }

  constructor(private rootStore: RootStore) {
    makeAutoObservable(this);
    autorun(() => {
      if (this.rootStore.accountStore.authenticated) {
        this.loadFarms()
          .then(() => this.selectInitialFarm())
          .catch((e) => {
            console.error("Failed to load farms", e);
            toast.error("Failed to load farms: " + e.toString());
          })
          .finally(() => {
            this.setLoading(false);
          });
      }
    });
  }

  // Restore the persisted selection if it still exists, otherwise fall back to
  // the Default Farm (or the first farm).
  private selectInitialFarm = () => {
    if (this.farms.length === 0) return;
    let stored: string | null = null;
    try {
      stored = window.localStorage.getItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    const match = stored && this.farms.find((f) => f.id === stored);
    const target = match || this.farms.find((f) => f.isDefault) || this.farms[0];
    this.setCurrentFarmId(target.id);
  };

  createFarm = async (name: string): Promise<string> => {
    const id = await createFarm(name);
    await this.loadFarms();
    this.setCurrentFarmId(id);
    return id;
  };

  renameFarm = async (id: string, name: string): Promise<void> => {
    await renameFarm(id, name);
    await this.loadFarms();
  };

  deleteFarm = async (id: string): Promise<void> => {
    await deleteFarm(id);
    // If we deleted the active farm, fall back to the default before reloading.
    if (this.currentFarmId === id) {
      const fallback = this.farms.find((f) => f.isDefault) || this.farms[0];
      if (fallback) this.setCurrentFarmId(fallback.id);
    }
    await this.loadFarms();
  };

  private loadFarms = async () => {
    return listFarms()
      .then((farms) => {
        this.setFarms(farms);
      })
      .catch((e) => {
        // api.ts already toasted the server's message.
        console.error("Failed to load farms list", e);
      });
  };
}

const ctx = React.createContext<FarmStore | null>(null);

export const useFarmsVM = () => useVM(ctx);

export const useFarmId = () => {
  const vm = useFarmsVM();
  return vm.currentFarmId;
};

export const ContextProvider: React.FC = ({ children }) => {
  const rootStore = useStores();
  return <ctx.Provider value={rootStore.farmStore}>{children}</ctx.Provider>;
};

export default FarmStore;
